import ActionButton from 'components/ActionButton';
import BackButton from 'components/BackButton';
import CityDropdown from 'components/CityDropdown';
import ImageUploadComponent from 'components/ImageUploadComponent';
import { Loading } from 'components/loading/Loading';
// import StateDropdown from 'components/StateDropdown';
import SupplierSchedule from 'components/SupplierSchedule';
import UsersTable from 'components/UsersTable';
import React, { useEffect, useState } from 'react';
import { addUserToSupplier, fetchSupplierDetails, fetchUsersForSupplier, removeUserFromSupplier, updateSupplier } from 'services/api/suppliers';
import { showError, showSuccess, stringifyAndParseObj } from 'utils';
import SupplierBlogs from './Blogs';
import styles from './index.module.scss';
import InventoryItems from './InventoryItems';
import _ from 'lodash';
import CustomLink from 'components/TableLink';
import SEOFields from './SEOFields';
import { PurchaseOrders } from 'pages/purchase-order';
import DynamicLink from 'components/DynamicLink';
import Checkbox from 'components/FormFields/checkbox';

const schedule: Record<string, any> = {
  sunday: false,
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false,
};

const LabelField = ({ value }: { value: string | number }) => (
  <div className={styles['label']}>{value}</div>
);

const InfoField = ({ value }: { value: string | number }) => (
  <div className={styles['info']}>{value || 'N/A'}</div>
);

const SupplierDetails = props => {
  const { match: { params: { supplierId }}} = props;
  const [loading, setLoading] = useState<boolean>(true);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [basicDetailsEditable, setBasicDetailsEditable] = useState<boolean>(false);
  const [supplier, setSupplier] = useState<Record<string, any>>({});
  const [addInventoryItemModalOpen, setAddInventoryItemModalOpen] = useState<boolean>(false);
  const [updatedSupplier, setUpdatedSupplier] = useState<Record<string, any>>({});
  const [editLocationDetails, setEditLocationDetails] = useState<boolean>(false);
  const [photoUpdateLoading, setPhotoUpdateLoading] = useState<boolean>(false);
  const photosChanged = () => {
    const bgPhoto = !_.isEqual(updatedSupplier.bgPhoto, supplier.bgPhoto);
    const profilePhoto = !_.isEqual(updatedSupplier.profilePhoto, supplier.profilePhoto);
    return bgPhoto || profilePhoto;
  };
  const PUBLIC_PROFILE_URL = `${process.env.REACT_APP_WEB_APP_URL}/producers/${supplier?.slug}`;

  const fetchSupplierDetailsFn = async () => {
    try {
      const response = await fetchSupplierDetails(supplierId);
      setSupplier(response);
      setUpdatedSupplier(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchSupplierDetailsFn();
  }, []);

  const updateSupplierInfo = async () => {
    setUpdateLoading(true);
    const supplierData = {
      ..._.omit(updatedSupplier, ['objectId']),
      id: supplierId,
    };
    try {
      await updateSupplier(supplierData);
      showSuccess('Supplier details updated successfully.');
      setSupplier({ ...supplier, ...updatedSupplier });
    } catch (e) {
      showError(e);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleEdit = async () => {
    if (basicDetailsEditable) {
      try {
        await updateSupplierInfo();
        setBasicDetailsEditable(false);
      } catch (e) {
        // 
      }
    } else {
      setBasicDetailsEditable(true);
    }
  };

  const handleLocationEdit = async () => {
    if (editLocationDetails) {
      try {
        await updateSupplierInfo();
        setEditLocationDetails(false);
      } catch (e) {
        // 
      }
    } else {
      setEditLocationDetails(true);
    }
  };
  
  const handlePhotoUpdate = async () => {
    setPhotoUpdateLoading(true);
    try {
      await updateSupplierInfo();
    } catch (e) {
      // 
    } finally {
      setPhotoUpdateLoading(false);
    }
  };

  const handleCancel = (defaultValue: Record<string, any>) => {
    setUpdatedSupplier({ ...updatedSupplier, ...defaultValue });
  };
  
  const getSchedule = () => (
    <div className={styles['supplier-schedule-section']}>
      <div className={styles['section-heading']}>Schedule</div>
      <div className={styles['section-details-container']}>
        <SupplierSchedule
          onChange={schedule => setSupplier({ ...supplier, schedule })}
          supplierId={supplierId}
          schedule={supplier.schedule || schedule}
        />
      </div>
    </div>
  );

  const getLocationSection = () => {
    const getLocationDetailsDisplaySection = () => (
      <div className={styles['section-details-container']}>
        <div className={styles['latitude-group']}>
          <LabelField value={'Latitude'} />
          <InfoField value={updatedSupplier.coords?.latitude} />
        </div>
        <div className={styles['longitude-group']}>
          <LabelField value={'Longitude'} />
          <InfoField value={updatedSupplier.coords?.longitude} />
        </div>
        <div className={styles['city-group']}>
          <LabelField value={'City'} />
          <InfoField value={updatedSupplier.city} />
        </div>
        {/* <div className={styles['state-group']}>
          <LabelField value={'State'} />
          <InfoField value={supplier.state} />
        </div> */}
      </div>
    );

    const getLocationDetailsInputSection = () => (
      <div className={styles['section-details-container']}>
        <div className={styles['latitude-group']}>
          <LabelField value={'Latitude'} />
          <input
            className={'form-control'}
            type={'number'}
            disabled={updateLoading}
            value={updatedSupplier.latitude}
            onChange={e => setUpdatedSupplier({
              ...updatedSupplier,
              coords: {
                ...updatedSupplier.coords,
                latitude: parseFloat(e.target.value),
              },
            })}
          />
        </div>
        <div className={styles['longitude-group']}>
          <LabelField value={'Longitude'} />
          <input
            className={'form-control'}
            type={'number'}
            disabled={updateLoading}
            value={updatedSupplier.longitude}
            onChange={e => setUpdatedSupplier({
              ...updatedSupplier,
              coords: {
                ...updatedSupplier.coords,
                longitude: parseFloat(e.target.value),
              },
            })}
          />
        </div>
        <div className={styles['city-group']}>
          <LabelField value={'City'} />
          <CityDropdown
            value={updatedSupplier.city}
            disabled={updateLoading}
            onChange={city => setUpdatedSupplier({ ...updatedSupplier, city })}
          />
        </div>
        {/* <div className={styles['state-group']}>
          <LabelField value={'State'} />
          <StateDropdown
            disabled={updateLoading}
            value={updatedSupplier.state}
            onChange={state => setUpdatedSupplier({ ...updatedSupplier, state })}
          />
        </div> */}
      </div>
    );

    return (
      <div className={styles['supplier-location-section']}>
        <div className={styles['section-header']}>
          <div className={styles['section-heading']}>Supplier Location</div>
          <div className={'d-flex'}>
            <ActionButton
              text={editLocationDetails ? 'Save' : 'Edit'}
              active={!updateLoading}
              isLoading={updateLoading}
              onClick={handleLocationEdit}
            />
            {editLocationDetails && (
              <ActionButton
                text={'Cancel'}
                active={true}
                isLoading={false}
                onClick={() => {
                  handleCancel({
                    city: supplier.city || '',
                    coords: {
                      latitude: supplier.coords?.latitude || undefined,
                      longitude: supplier.coords?.longitude || undefined,
                    },
                  });
                  setEditLocationDetails(false);
                }}
                customClass={styles['cancel-button']}
              />
            )}
          </div>
        </div>
        {editLocationDetails ? getLocationDetailsInputSection() : getLocationDetailsDisplaySection()}
      </div>
    );
  };

  const getSEOFields = () => (
    <SEOFields
      updatedSupplier={updatedSupplier}
      supplier={supplier}
      handleEditCancel={handleCancel}
      loading={updateLoading}
      setUpdatedSupplier={setUpdatedSupplier}
      updateSupplierFn={updateSupplierInfo}
    />
  );
  
  const getBasicDetails = () => {
    const getBasicDetailsDisplaySection = () => (
      <div className={styles['details-section']}>
        <div>
          <div className={styles['supplier-name']}>
            <LabelField value={'Supplier Name'} />
            <InfoField value={updatedSupplier.name} />
          </div>
          <div className={styles['supplier-email']}>
            <LabelField value={'Email'} />
            <InfoField value={updatedSupplier.email} />
          </div>
          <div className={styles['supplier-address']}>
            <LabelField value={'Address'} />
            <InfoField value={updatedSupplier.address} />
          </div>
          <div className={styles['facebook-handle']}>
            <LabelField value={'Facebook Handle'} />
            <CustomLink
              text={updatedSupplier.fbHandle}
              linkTo={updatedSupplier.fbHandle} />
          </div>
          <div className={styles['instagram-handle']}>
            <LabelField value={'Instagram Handle'} />
            <CustomLink
              text={updatedSupplier.instaHandle}
              linkTo={updatedSupplier.instaHandle} />
          </div>
          <div className={styles['supplier-webapp-link']}>
            <LabelField value={'Public Profile Link'} />
            <CustomLink
              text={PUBLIC_PROFILE_URL}
              linkTo={PUBLIC_PROFILE_URL} />
          </div>
          <DynamicLink link={updatedSupplier?.dynamicLink} />
        </div>
        <div>
          {/* <EditActions /> */}
          <div className={styles['supplier-phone']}>
            <LabelField value={'Phone'} />
            <InfoField value={updatedSupplier.phone} />
          </div>
          <div className={styles['supplier-bank-account-number']}>
            <LabelField value={'Bank Account Number'} />
            <InfoField value={updatedSupplier.bankAccountNo} />
          </div>
          <div className={styles['supplier-bank-routing-number']}>
            <LabelField value={'Bank Routing Number'} />
            <InfoField value={updatedSupplier.bankRoute} />
          </div>
          <div className={styles['supplier-featured']}>
            <LabelField value={'Featured'} />
            <InfoField value={updatedSupplier.featured?.toString() || 'False'} />
          </div>
          <div className={styles['supplier-visible']}>
            <LabelField value={'Visible'} />
            <InfoField value={updatedSupplier.visible?.toString() || 'True'} />
          </div>
        </div>
      </div>
    );

    const getBasicDetailsInputsSection = () => (
      <div className={styles['details-section']}>
        <div className={''}>
          <div className={styles['supplier-name']}>
            <LabelField value={'Supplier Name'} />
            <input
              className={'form-control'}
              disabled={updateLoading}
              value={updatedSupplier.name}
              onChange={e => setUpdatedSupplier({ ...updatedSupplier, name: e.target.value })}
            />
          </div>
          <div className={styles['supplier-email']}>
            <LabelField value={'Email'} />
            <input
              className={'form-control'}
              disabled={updateLoading}
              value={updatedSupplier.email}
              onChange={e => setUpdatedSupplier({ ...updatedSupplier, email: e.target.value })}
            />
          </div>
          <div className={styles['supplier-address']}>
            <LabelField value={'Address'} />
            <textarea
              className={'form-control'}
              disabled={updateLoading}
              value={updatedSupplier.address}
              onChange={e => setUpdatedSupplier({ ...updatedSupplier, address: e.target.value })}
            />
          </div>
          <div className={styles['facebook-handle']}>
            <LabelField value={'Facebook Handle'} />
            <input
              className={'form-control'}
              disabled={updateLoading}
              value={updatedSupplier.fbHandle}
              onChange={e => setUpdatedSupplier({ ...updatedSupplier, fbHandle: e.target.value })}
            />
          </div>
          <div className={styles['instagram-handle']}>
            <LabelField value={'Instagram Handle'} />
            <input
              className={'form-control'}
              disabled={updateLoading}
              value={updatedSupplier.instaHandle}
              onChange={e => setUpdatedSupplier({ ...updatedSupplier, instaHandle: e.target.value })}
            />
          </div>
          <DynamicLink link={updatedSupplier?.dynamicLink}/>
        </div>
        <div className={''}>
          {/* <EditActions /> */}
          <div className={styles['supplier-phone']}>
            <LabelField value={'Phone'} />
            <input
              className={'form-control'}
              disabled={updateLoading}
              value={updatedSupplier.phone}
              onChange={e => setUpdatedSupplier({ ...updatedSupplier, phone: e.target.value })}
            />
          </div>
          <div className={styles['supplier-bank-account-number']}>
            <LabelField value={'Bank Account Number'} />
            <input
              className={'form-control'}
              disabled={updateLoading}
              value={updatedSupplier.bankAccountNo}
              onChange={e => setUpdatedSupplier({ ...updatedSupplier, bankAccountNo: e.target.value })}
            />
          </div>
          <div className={styles['supplier-bank-routing-number']}>
            <LabelField value={'Bank Routing Number'} />
            <input
              className={'form-control'}
              disabled={updateLoading}
              value={updatedSupplier.bankRoute}
              onChange={e => setUpdatedSupplier({ ...updatedSupplier, bankRoute: e.target.value })}
            />
          </div>
          <div className={styles['supplier-featured']}>
            <Checkbox title="Featured" isChecked={updatedSupplier?.featured || false} setIsChecked={(checked) => setUpdatedSupplier({ ...updatedSupplier, featured: checked })} />
          </div>
          <div className={styles['supplier-visible']}>
            <Checkbox title="Visible" isChecked={updatedSupplier?.visible?.toString() ? updatedSupplier?.visible : true} setIsChecked={(checked) => setUpdatedSupplier({ ...updatedSupplier, visible: checked })} />
          </div>
        </div>
      </div>
    );

    return (
      <div className={styles['basic-details-container']}>
        <div>
          <div className={styles['supplier-id']}>
            <LabelField value={'Supplier ID'} />
            <div className={styles['info']}>{supplierId}</div>
          </div>
          <div className={'d-flex'}>
            <ActionButton
              text={basicDetailsEditable ? 'Save' : 'Edit'}
              active={!updateLoading}
              isLoading={updateLoading}
              onClick={handleEdit}
              customClass={'mt-0'}
            />
            {basicDetailsEditable && (
              <ActionButton
                text={'Cancel'}
                active={true}
                isLoading={false}
                onClick={() => {
                  handleCancel({
                    name: supplier.name || '',
                    email: supplier.email || '',
                    address: supplier.address || '',
                    description: supplier.description || '',
                    fbHandle: supplier.fbHandle || '',
                    instaHandle: supplier.instaHandle || '',
                    phone: supplier.phone || '',
                    bankAccountNo: supplier.bankAccountNo || '',
                    bankRoute: supplier.bankRoute || '',
                  });
                  setBasicDetailsEditable(false);
                }}
                customClass={styles['cancel-button']}
              />
            )}
          </div>
        </div>
        {basicDetailsEditable ? getBasicDetailsInputsSection() : getBasicDetailsDisplaySection()}
      </div>
    );
  };

  const getPhotosSection = () => (
    <div className={styles['supplier-photos-section']}>
      <div>
        {photosChanged() && (
          <div className={'d-flex'}>
            <ActionButton
              text={'Save'}
              active={!photoUpdateLoading}
              isLoading={photoUpdateLoading}
              onClick={handlePhotoUpdate}
            />
            {/* <ActionButton
              text={'Cancel'}
              active={true}
              isLoading={false}
              onClick={() => {
                handleCancel({
                  bgPhoto: supplier.bgPhoto || {},
                  profilePhoto: supplier.profilePhoto || {},
                });
              }}
              customClass={styles['cancel-button']}
            /> */}
          </div>
        )}
      </div>
      <div>
        <div className={styles['avatar-image']}>
          <LabelField value={'Profile Photo'} />
          <ImageUploadComponent
            src={updatedSupplier.profilePhoto?.url}
            entityId={'profilePhoto'}
            toggleModal={() => {}}
            uploadAPI={(id, image) => setUpdatedSupplier({ ...updatedSupplier, profilePhoto: stringifyAndParseObj(image) })}
          />
        </div>
        <div className={styles['background-image']}>
          <LabelField value={'Background Photo'} />
          <ImageUploadComponent
            src={updatedSupplier.bgPhoto?.url}
            entityId={'bgPhoto'}
            toggleModal={() => {}}
            uploadAPI={(id, image) => setUpdatedSupplier({ ...updatedSupplier, bgPhoto: stringifyAndParseObj(image) })}
          />
        </div>
      </div>
    </div>
  );
  
  const Header = () => (
    <div className={styles['header']}>
      <div className={styles['heading']}>Supplier Details</div>
      <BackButton />
    </div>
  );
  
  if (loading) return <Loading />;
  
  return (
    <div className={`${styles['main-container']}`}>
      <Header />
      {getPhotosSection()}
      {getBasicDetails()}
      {getSEOFields()}
      {getLocationSection()}
      {getSchedule()}
      <SupplierBlogs
        blogs={updatedSupplier.blogs}
        supplierId={supplierId}
        onSuccess={val => {
          setSupplier(val);
          setUpdatedSupplier(val);
        }}
      />
      <UsersTable
        entityId={supplierId}
        entityKey={'supplierId'}
        entityName={'Supplier'}
        addUserAPI={addUserToSupplier}
        deleteUserAPI={removeUserFromSupplier}
        fetchUsersAPI={fetchUsersForSupplier}
      />
      <InventoryItems
        {...props}
        supplierId={supplierId}
        addInventoryItemModalOpen={addInventoryItemModalOpen}
        setAddInventoryItemModalOpen={setAddInventoryItemModalOpen}
      />
      <PurchaseOrders
        {...props}
      />
    </div>
  );
};

export { SupplierDetails };
